import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Button,
  Modal,
  Form,
  Select,
  message,
  Spin,
  Alert,
  Space,
  Divider,
  Tag,
} from "antd";
import { debounce } from "lodash";
import axios from "axios";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";

const { Option } = Select;

const ELIGIBLE_ENDPOINT = "/api/users/eligible-main-account";

const REASON_LABELS = {
  hasOrders: "Account has orders",
  hasCredit: "Account has credit balance",
  hasPendingCod: "Account has pending COD",
  hasIntegrations: "Account has Shopify/WooCommerce connected",
  hasSubaccounts: "Account has subaccounts",
  noMerchant: "Account has no merchant",
};

const ROLE_OPTIONS = [
  { key: "admin", label: "Admin" },
  { key: "operation", label: "Operation" },
  { key: "finance", label: "Finance" },
];

function MainAccountPickerModal({
  title,
  okText,
  submitEndpoint,
  excludeIds = [],
  onCancel,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requestSeq = useRef(0);

  const serverExcludeId = excludeIds[0];

  const fetchEligible = useCallback(
    async (search) => {
      const seq = ++requestSeq.current;
      setSearching(true);
      try {
        const params = { eligibleMainAccount: true };
        if (search) params.search = search;
        if (serverExcludeId) params.excludeUserId = serverExcludeId;
        const res = await axios.get(ELIGIBLE_ENDPOINT, { params });
        if (seq !== requestSeq.current) return;
        const list = res.data?.data || [];
        setOptions(list.filter((item) => !excludeIds.includes(item.id)));
      } catch (err) {
        if (seq !== requestSeq.current) return;
        const msg = err.response?.data?.message || err.message;
        message.error(msg || "Failed to load eligible main accounts");
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    },
    // excludeIds is a fresh array each render; key on its serialized content instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverExcludeId, excludeIds.join(",")]
  );

  const debouncedSearch = useMemo(
    () => debounce((value) => fetchEligible(value), 400),
    [fetchEligible]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    form.resetFields();
    fetchEligible("");
  }, [fetchEligible, form]);

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(submitEndpoint, {
        mainAccountId: values.mainAccountId,
        role: values.role,
      });
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      message.error(msg || "Failed to update subaccount");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      visible
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={submitting}
      okText={okText}
      forceRender
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="mainAccountId"
          label="Target Main Account"
          rules={[{ required: true, message: "Please select a main account" }]}
        >
          <Select
            showSearch
            placeholder="Search by email or name"
            filterOption={false}
            onSearch={debouncedSearch}
            loading={searching}
            defaultActiveFirstOption={false}
            notFoundContent={searching ? <Spin size="small" /> : null}
          >
            {options.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name} ({item.email})
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: "Please select a role" }]}
        >
          <Select placeholder="Select role">
            {ROLE_OPTIONS.map((r) => (
              <Option key={r.key} value={r.key}>
                {r.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}

function UserSubAccountActions({ userId, onChanged }) {
  const { permissions } = usePermissions();
  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [picker, setPicker] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const contextSeq = useRef(0);

  const hasManagePermission = permissions?.includes(PERMISSIONS.MANAGE_SUBACCOUNT);

  const fetchContext = useCallback(async () => {
    if (!userId || !hasManagePermission) return;
    const seq = ++contextSeq.current;
    setLoadingContext(true);
    try {
      const res = await axios.get(`/api/users/${userId}/subaccount-context`);
      if (seq !== contextSeq.current) return;
      setContext(res.data?.data || null);
    } catch (err) {
      if (seq !== contextSeq.current) return;
      setContext(null);
      const msg = err.response?.data?.message || err.message;
      message.error(msg || "Failed to load subaccount context");
    } finally {
      if (seq === contextSeq.current) setLoadingContext(false);
    }
  }, [userId, hasManagePermission]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  const handleConvert = () => {
    if (convertOpen || converting) return;
    setConvertOpen(true);
    Modal.confirm({
      title: "Convert to Main Account",
      content:
        "This starts a fresh standalone account with zero credit and default package; past orders/statements are not moved.",
      okText: "Convert",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setConverting(true);
        try {
          await axios.post(`/api/users/${userId}/subaccount/convert-to-main`, {});
          message.success("Converted to main account successfully");
          setConvertOpen(false);
          await fetchContext();
          onChanged && onChanged();
        } catch (err) {
          const msg = err.response?.data?.message || err.message;
          message.error(msg || "Failed to convert subaccount");
          throw err;
        } finally {
          setConverting(false);
        }
      },
      onCancel: () => setConvertOpen(false),
    });
  };

  if (!hasManagePermission) return null;

  const isEmptyReasons = context?.emptyCheck?.reasons || [];
  const canLinkDisabledReason =
    context && !context.canLink
      ? context.isSubAccount
        ? "This account is already a subaccount"
        : isEmptyReasons.length > 0
        ? isEmptyReasons.map((r) => REASON_LABELS[r] || r).join(", ")
        : "Not eligible to link"
      : null;

  const handlePickerSuccess = async (text) => {
    message.success(text);
    setPicker(null);
    await fetchContext();
    onChanged && onChanged();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Divider orientation="left">Subaccount Management</Divider>
      {loadingContext && <Spin />}
      {context && (
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div>
              <strong>Status: </strong>
              {context.isSubAccount ? (
                <Tag color="blue">Subaccount</Tag>
              ) : (
                <Tag color="green">Main Account</Tag>
              )}
            </div>
            {context.isSubAccount && context.mainAccountId && (
              <div>
                <strong>Main Account ID: </strong>
                {context.mainAccountId}
              </div>
            )}
            {context.merchantId && (
              <div>
                <strong>Merchant ID: </strong>
                {context.merchantId}
              </div>
            )}
          </Space>
          {canLinkDisabledReason && (
            <Alert
              type="info"
              message="Cannot link"
              description={canLinkDisabledReason}
              style={{ marginTop: 12 }}
              showIcon
            />
          )}
        </div>
      )}
      <Space wrap>
        <Button
          type="primary"
          onClick={() => setPicker("link")}
          disabled={!context?.canLink}
        >
          Link to Main Account
        </Button>
        <Button
          type="default"
          onClick={() => setPicker("move")}
          disabled={!context?.canMove}
        >
          Move Subaccount
        </Button>
        <Button
          type="danger"
          onClick={handleConvert}
          loading={converting}
          disabled={!context?.canConvert}
        >
          Convert to Main
        </Button>
      </Space>

      {picker === "link" && (
        <MainAccountPickerModal
          title="Link to Main Account"
          okText="Link"
          submitEndpoint={`/api/users/${userId}/subaccount/link`}
          excludeIds={[userId]}
          onCancel={() => setPicker(null)}
          onSuccess={() => handlePickerSuccess("Subaccount linked successfully")}
        />
      )}

      {picker === "move" && (
        <MainAccountPickerModal
          title="Move Subaccount"
          okText="Move"
          submitEndpoint={`/api/users/${userId}/subaccount/move`}
          excludeIds={[userId, context?.mainAccountId].filter(Boolean)}
          onCancel={() => setPicker(null)}
          onSuccess={() => handlePickerSuccess("Subaccount moved successfully")}
        />
      )}
    </div>
  );
}

export default UserSubAccountActions;
